import { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  FaChartBar, FaUserPlus, FaClipboardList, FaDollarSign, FaLink,
  FaCheckCircle, FaStar, FaDrumstickBite, FaBullseye,
  FaChartLine, FaEnvelope, FaCreditCard,
} from '../lib/icons';
import { AdminMe, logoutAdminPortal } from '../lib/admin-portal-api';

interface Props {
  me: AdminMe;
  children: ReactNode;
  active: string;
}

const navItems = [
  { to: '/admin-portal',              label: 'Dashboard',     IconC: FaChartBar,       end: true },
  { to: '/admin-portal/users',        label: 'Users',         IconC: FaUserPlus },
  { to: '/admin-portal/community',    label: 'Community',     IconC: FaClipboardList },
  { to: '/admin-portal/subscriptions',label: 'Subscriptions', IconC: FaStar },
  { to: '/admin-portal/recipes',      label: 'Recipes',       IconC: FaDrumstickBite },
  { to: '/admin-portal/referrals',    label: 'Referrals',     IconC: FaDollarSign, superOnly: true },
  { to: '/admin-portal/plans',        label: 'Plans',         IconC: FaCreditCard, superOnly: true },
  { to: '/admin-portal/bulk-email',   label: 'Bulk email',    IconC: FaEnvelope,   superOnly: true },
  { to: '/admin-portal/analytics',    label: 'Analytics',     IconC: FaChartLine,  superOnly: true },
  { to: '/admin-portal/settings',     label: 'Settings',      IconC: FaBullseye,   superOnly: true },
];

export default function AdminPortalLayout({ me, children }: Props) {
  const navigate = useNavigate();
  const handleLogout = () => {
    logoutAdminPortal();
    navigate('/admin-portal/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/admin-portal" className="font-extrabold tracking-tight text-gray-900">
              EatMeat <span className="text-primary">Admin</span>
            </Link>
            <span className="ml-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              <FaCheckCircle /> {me.isSuperAdmin ? 'Super Admin' : 'Staff'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${process.env.REACT_APP_STRAPI_API_URL || ''}/admin`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-gray-500 hover:text-primary inline-flex items-center gap-1"
              title="Open the underlying Strapi admin"
            >
              <FaLink /> Strapi admin
            </a>
            <span className="text-xs text-gray-500 hidden sm:inline">{me.email}</span>
            <button onClick={handleLogout} className="text-xs text-gray-500 hover:text-red-500 font-semibold">
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <nav className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2 sticky top-20">
            {navItems
              .filter((i) => !i.superOnly || me.isSuperAdmin)
              .map((i) => {
                const I = i.IconC;
                return (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    end={i.end}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                        isActive ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-gray-50'
                      }`
                    }
                  >
                    <I /> {i.label}
                  </NavLink>
                );
              })}
          </nav>
        </aside>

        {/* Main */}
        <main className="col-span-12 md:col-span-9 lg:col-span-10 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
